import { useEffect, useState } from "react"
import { Building2, CheckCircle2, Loader2, Plus } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import { EmptyState } from "../../components/ui/EmptyState"
import { fr } from "../../locales/fr"
import { useAuth } from "../../context/AuthContext"
import { createGym, getAccessibleGyms, switchGym } from "../../services/gymService"
import { isOwner } from "../../utils/roles"

const emptyForm = {
  name: "",
  address: "",
  city: "",
  phone: "",
  email: "",
}

export default function SettingsGyms() {
  const { user, activeGym } = useAuth()
  const canCreate = isOwner(user)
  const [gyms, setGyms] = useState(user?.gyms ?? [])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [switchingId, setSwitchingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState("")

  const load = async () => {
    setLoading(true)
    try {
      const data = await getAccessibleGyms()
      setGyms(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setGyms(user?.gyms ?? [])
    }
    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  const handleCreate = async (event) => {
    event.preventDefault()
    if (!canCreate) return
    setSaving(true)
    setError("")
    try {
      const created = await createGym({
        name: form.name.trim(),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      })
      setForm(emptyForm)
      await switchGym(created.id)
    } catch (e) {
      console.error(e)
      setError(e?.response?.data?.error || fr.gyms.createError)
      setSaving(false)
    }
  }

  const handleSwitch = async (gymId) => {
    if (gymId === activeGym?.id) return
    setSwitchingId(gymId)
    try {
      await switchGym(gymId)
    } catch (e) {
      console.error(e)
      setSwitchingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{fr.gyms.title}</h1>
        <p className="text-muted-foreground">{fr.gyms.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{fr.gyms.listTitle}</CardTitle>
          <CardDescription>{fr.gyms.listDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">{fr.common.loading}</p>
          ) : gyms.length === 0 ? (
            <EmptyState title={fr.gyms.empty} />
          ) : (
            <ul className="space-y-3">
              {gyms.map((gym) => {
                const isActive = gym.id === activeGym?.id
                return (
                  <li
                    key={gym.id}
                    className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                        <Building2 className="h-5 w-5" aria-hidden />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{gym.name}</p>
                        {gym.city && (
                          <p className="text-sm text-muted-foreground">{gym.city}</p>
                        )}
                        {Array.isArray(gym.roles) && gym.roles.length > 0 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {fr.gyms.rolesLabel}: {gym.roles.join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 text-sm text-primary">
                          <CheckCircle2 className="h-4 w-4" aria-hidden />
                          {fr.gyms.activeGym}
                        </span>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={switchingId === gym.id}
                          onClick={() => void handleSwitch(gym.id)}
                        >
                          {switchingId === gym.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            fr.gyms.switchTo
                          )}
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {canCreate && (
        <Card>
          <CardHeader>
            <CardTitle>{fr.gyms.createTitle}</CardTitle>
            <CardDescription>{fr.gyms.createDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.gyms.formName}</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.gyms.formCity}</label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.gyms.formPhone}</label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.gyms.formEmail}</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.gyms.formAddress}</label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              {error && (
                <p className="text-sm text-destructive md:col-span-2">{error}</p>
              )}
              <div className="md:col-span-2">
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {fr.common.saving}
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      {fr.gyms.createButton}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
